import type { MatchEvent, MatchState } from '../../engine';
import type { Profile } from '../types';
import { APPRAISALS, PLEDGES, type PledgeId } from './catalog';
import type { LedgerProfile, LedgerStats, NightRun } from './types';

/**
 * Meta-progression (docs/SHOP_DESIGN.md §14, §16): the gradual reveal, the Pledges to unlock,
 * and the items the Ledger collection keeps behind a condition. Unlocks broaden the game; they
 * never make a run stronger by themselves.
 */

/** Items that wait behind a condition: the stat and the value it must reach. */
export const ITEM_UNLOCKS: Readonly<
  Record<string, { readonly stat: keyof LedgerStats; readonly at: number }>
> = {
  bloodline: { stat: 'monochromeRounds', at: 1 },
  'seance-table': { stat: 'epitaphReplays', at: 3 },
  'sacrificial-lamb': { stat: 'pawnedInATable', at: 3 },
  'chain-letter': { stat: 'ladders', at: 1 },
  'golden-tooth': { stat: 'mostBrass', at: 50 },
  'black-mirror': { stat: 'fullCircles', at: 1 },
  'skeleton-key': { stat: 'capturesInARound', at: 5 },
  'double-knot': { stat: 'patternsScored', at: 25 },
  'rabbits-foot': { stat: 'chancesHit', at: 5 },
  'mimic-jar': { stat: 'wins', at: 1 },
  reliquary: { stat: 'wins', at: 1 },
};

/** Pledge unlock conditions (docs/SHOP_DESIGN.md §12.1). */
export const PLEDGE_UNLOCKS: Readonly<
  Record<Exclude<PledgeId, 'pocket-watch'>, (l: LedgerProfile, p: Profile) => boolean>
> = {
  'wedding-ring': (l) => l.stats.epitaphReplays >= 5,
  fiddle: (l) => l.stats.streaks >= 10,
  locket: (_l, p) => p.collection.length > 0,
  dice: (l) => l.stats.chancesHit >= 10,
  'ivory-set': (l) => l.stats.crownsInATable >= 6,
  'glass-eye': (l) => l.stats.omensLanded >= 20,
  iou: (l) => l.stats.debts >= 1,
  'bare-box': (l) => l.stats.wins >= 1,
  scale: (l) => Object.values(l.terms).some((t) => t >= 4),
  'shuffled-box': (l) => Object.values(l.terms).filter((t) => t >= 1).length >= 3,
  ash: (l) => Object.values(l.terms).some((t) => t >= 6),
};

export const isItemUnlocked = (l: LedgerProfile, id: string): boolean =>
  !ITEM_UNLOCKS[id] || l.unlocked.includes(`item:${id}`);

/** The feature an item or Lot belongs to, for the gradual reveal (§16). */
export const FEATURES = [
  'curios',
  'stamps',
  'carvings',
  'omens',
  'back-room',
  'detours',
  'estate',
  'tricks',
  'terms',
  'after-hours',
  'daily',
] as const;

/** Counts what a finished table showed, for the unlock conditions. */
export function noteStats(
  l: LedgerProfile,
  run: NightRun,
  events: readonly MatchEvent[],
  final: MatchState,
): void {
  const st = l.stats;
  let crowns = 0;
  let pawned = 0;
  let captures = 0;
  let colors: string[] = [];
  const patterns = new Map<string, number>();
  const closeRound = () => {
    st.capturesInARound = Math.max(st.capturesInARound, captures);
    if (colors.length >= 6 && colors.every((c) => c === colors[0])) st.monochromeRounds += 1;
    captures = 0;
    colors = [];
  };
  for (const e of events) {
    switch (e.type) {
      case 'RoundStarted':
        closeRound();
        break;
      case 'ChainScored': {
        if (e.side !== 'S') break;
        const m = e.breakdown.multiplier;
        if (m.pattern) {
          st.patternsScored += 1;
          patterns.set(m.pattern, (patterns.get(m.pattern) ?? 0) + 1);
        }
        if (m.pattern === 'ladder') st.ladders += 1;
        if (m.pattern === 'streak') st.streaks += 1;
        if (m.fullCircle) st.fullCircles += 1;
        st.chancesHit += (e.breakdown.steps ?? []).filter((x) => x.chance).length;
        break;
      }
      case 'PawnCrowned':
        if (e.side === 'S') crowns += 1;
        break;
      case 'PawnPawned':
        if (e.side === 'S') pawned += 1;
        break;
      case 'PawnCaptured':
        if (e.side === 'N') captures += 1;
        break;
      case 'LinkPlayed':
        if (e.side === 'S') colors.push(e.step.face.color);
        break;
      case 'EpitaphPlayed':
        if (e.side === 'S') st.epitaphReplays += 1;
        break;
      case 'OmenTriggered':
        if (e.side === 'S') st.omensLanded += 1;
        break;
      default:
        break;
    }
  }
  closeRound();
  st.crownsInATable = Math.max(st.crownsInATable, crowns);
  st.pawnedInATable = Math.max(st.pawnedInATable, pawned);
  st.mostBrass = Math.max(st.mostBrass, final.ledger?.brass.S ?? 0);
  let most: string | null = null;
  for (const [k, v] of patterns) if (!most || v > (patterns.get(most) ?? 0)) most = k;
  if (most) run.counters.lastPatternTarget = APPRAISALS.indexOf(most as never) + 1;
}

/** Unlocks what the profile has earned: features, Pledges and items. */
export function checkUnlocks(p: Profile): void {
  const l = p.ledger;
  if (!l) return;
  const add = (key: string) => {
    if (!l.unlocked.includes(key)) l.unlocked.push(key);
  };
  const run = l.run;
  const highWon =
    l.stats.wins > 0 ||
    (run?.outcomes.some((o) => (o.kind === 'high' || o.kind === 'closing') && o.won) ?? false) ||
    l.unlocked.includes('curios');
  if (highWon) {
    add('curios');
    add('stamps');
  }
  if (l.stats.deepestNight >= 2) {
    add('carvings');
    add('omens');
    add('back-room');
  }
  if (l.stats.deepestNight >= 3) add('tricks');
  if (l.stats.deepestNight >= 4) {
    add('detours');
    add('estate');
  }
  if (l.stats.wins >= 1) {
    add('terms');
    add('after-hours');
    add('daily');
  }
  for (const id of PLEDGES)
    if (id !== 'pocket-watch' && PLEDGE_UNLOCKS[id](l, p)) add(`pledge:${id}`);
  for (const [id, cond] of Object.entries(ITEM_UNLOCKS))
    if (l.stats[cond.stat] >= cond.at) add(`item:${id}`);
}
